/**
 * ☁️ Firebase Functions Client
 *
 * يهيّئ الـ httpsCallable لاستدعاء Cloud Functions.
 * Region: us-central1 (لازم يطابق منطقة نشر الـ Cloud Functions — Gen 1).
 */

import { getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { app } from './config';
import type {
  IncludedServices,
  MenIncludedServices,
  WomenIncludedServices,
} from '@domain/value-objects/IncludedServices';
import type {
  DayTypeValue,
  SeasonValue,
  SectionModeValue,
} from '@domain/value-objects/PackageEnums';

const FUNCTIONS_REGION = 'us-central1';

const functions: Functions = getFunctions(app, FUNCTIONS_REGION);

// ⚠️ دوال me-central1 (v2 — الأقرب للسعودية) لها instance منفصل:
//    markContacted / blockDate / rejectBookingRequest (رُقّيت لـ v2) + markDateAsBooked.
//    باقي الدوال (provider/admin/cities) لسه v1 على us-central1.
const FUNCTIONS_REGION_ME = 'me-central1';
const functionsMe: Functions = getFunctions(app, FUNCTIONS_REGION_ME);

// ═══════════════════════════════════════════════════
// Venue Location Utilities
// ═══════════════════════════════════════════════════

export interface ResolveGoogleMapsUrlInput {
  mapsUrl: string;
}

export interface ResolveGoogleMapsUrlResult {
  coordinates: {
    latitude: number;
    longitude: number;
  };
  mapsUrl: string;
  district: string | null;
  address: string | null;
}

export const callResolveGoogleMapsUrl = httpsCallable<
  ResolveGoogleMapsUrlInput,
  ResolveGoogleMapsUrlResult
>(functions, 'resolveGoogleMapsUrl');

// ═══════════════════════════════════════════════════
// Venue Packages (server-owned atomic mutations)
// ═══════════════════════════════════════════════════

export interface VenuePackageMutationData {
  name: string;
  price: number;
  capacityMin?: number;
  capacityMax?: number;
  capacityMenMin?: number;
  capacityMenMax?: number;
  capacityWomenMin?: number;
  capacityWomenMax?: number;
  sectionMode: SectionModeValue;
  dayType: DayTypeValue;
  season: SeasonValue;
  hasIncludedServices: boolean;
  includedServicesText?: string;
  includedServices?: IncludedServices;
  includedServicesMenText?: string;
  includedServicesMen?: MenIncludedServices;
  includedServicesWomenText?: string;
  includedServicesWomen?: WomenIncludedServices;
  notes?: string;
}

export interface CreateVenuePackageInput {
  venueId: string;
  package: VenuePackageMutationData;
}

export interface UpdateVenuePackageInput extends CreateVenuePackageInput {
  oldKey: string;
}

export interface DeleteVenuePackageInput {
  venueId: string;
  key: string;
}

export interface BulkUpdateVenuePackagePricesInput {
  venueId: string;
  prices: Array<{ key: string; price: number }>;
}

export interface VenuePackageMutationResult {
  success: true;
  key?: string;
  previousKey?: string;
  updated?: number;
}

export const callCreateVenuePackage = httpsCallable<
  CreateVenuePackageInput,
  VenuePackageMutationResult
>(functionsMe, 'createVenuePackage');

export const callUpdateVenuePackage = httpsCallable<
  UpdateVenuePackageInput,
  VenuePackageMutationResult
>(functionsMe, 'updateVenuePackage');

export const callDeleteVenuePackage = httpsCallable<
  DeleteVenuePackageInput,
  VenuePackageMutationResult
>(functionsMe, 'deleteVenuePackage');

export const callBulkUpdateVenuePackagePrices = httpsCallable<
  BulkUpdateVenuePackagePricesInput,
  VenuePackageMutationResult
>(functionsMe, 'bulkUpdateVenuePackagePrices');

// ═══════════════════════════════════════════════════
// Provider Management Functions
// ═══════════════════════════════════════════════════

export interface CreateProviderWithOwnerInput {
  businessName: string;
  providerType?:
    | 'venue'
    | 'hospitality'
    | 'photography'
    | 'decoration'
    | 'transport'
    | 'other'
    | undefined;
  city: string;
  shortDescription?: string | undefined;
  commercialRegister?: string | undefined;
  vatNumber?: string | undefined;
  logoUrl?: string | undefined;
  ownerEmail: string;
  ownerFullName: string;
  ownerPhone: string;
  phoneVerificationId: string;
  contactPhone?: string | undefined;
  contactEmail?: string | undefined;
}

export interface CreateProviderWithOwnerResult {
  providerId: string;
  ownerUid: string;
  /**
   * 📧 بريد المالك (مطبّع). لم نعُد نُرجع رابط reset للأدمن؛ العميل يرسل رابط
   * تعيين كلمة السر تلقائياً إلى هذا البريد عبر Firebase.
   */
  ownerEmail: string;
}

/**
 * 🏛️ إنشاء مزود جديد + مستخدم مالك
 * (admin only — التحقق في Cloud Function)
 */
export const callCreateProviderWithOwner = httpsCallable<
  CreateProviderWithOwnerInput,
  CreateProviderWithOwnerResult
>(functions, 'createProviderWithOwner');

export interface SendProviderCreationOtpInput {
  phone: string;
  lang?: string;
}

export interface SendProviderCreationOtpResult {
  success: true;
}

export const callSendProviderCreationOtp = httpsCallable<
  SendProviderCreationOtpInput,
  SendProviderCreationOtpResult
>(functions, 'sendProviderCreationOtp');

export interface VerifyProviderCreationOtpInput {
  phone: string;
  code: string;
}

export interface VerifyProviderCreationOtpResult {
  success: true;
  verificationId: string;
}

export const callVerifyProviderCreationOtp = httpsCallable<
  VerifyProviderCreationOtpInput,
  VerifyProviderCreationOtpResult
>(functions, 'verifyProviderCreationOtp');

export interface SendProviderUserPhoneOtpInput {
  phone: string;
  lang?: string;
}

export interface SendProviderUserPhoneOtpResult {
  success: true;
}

export const callSendProviderUserPhoneOtp = httpsCallable<
  SendProviderUserPhoneOtpInput,
  SendProviderUserPhoneOtpResult
>(functions, 'sendProviderUserPhoneOtp');

export interface VerifyProviderUserPhoneOtpInput {
  phone: string;
  code: string;
}

export interface VerifyProviderUserPhoneOtpResult {
  success: true;
  verificationId: string;
}

export const callVerifyProviderUserPhoneOtp = httpsCallable<
  VerifyProviderUserPhoneOtpInput,
  VerifyProviderUserPhoneOtpResult
>(functions, 'verifyProviderUserPhoneOtp');

export interface UpdateProviderUserInput {
  uid: string;
  fullName?: string;
  phone?: string;
  phoneVerificationId?: string;
  role?: string;
  permissions?: string[];
  status?: 'active' | 'suspended';
  mustChangePassword?: boolean;
}

export interface UpdateProviderUserResult {
  success: true;
}

export const callUpdateProviderUser = httpsCallable<
  UpdateProviderUserInput,
  UpdateProviderUserResult
>(functions, 'updateProviderUser');

export interface CheckProviderOwnerEmailInput {
  email: string;
}

export interface CheckProviderOwnerEmailResult {
  /** true = الإيميل صحيح وغير مستخدم → يصلح لإنشاء مالك جديد */
  available: boolean;
}

/**
 * 📧 التحقق (live) من صلاحية إيميل المالك + عدم استخدامه سلفاً.
 * (admin only) — يفعّل زر "إنشاء المزود" نظير OTP الواتساب للرقم.
 */
export const callCheckProviderOwnerEmailAvailable = httpsCallable<
  CheckProviderOwnerEmailInput,
  CheckProviderOwnerEmailResult
>(functions, 'checkProviderOwnerEmailAvailable');

export interface SyncProviderPasswordChangeStatusResult {
  completed: boolean;
}

export const callSyncProviderPasswordChangeStatus = httpsCallable<
  Record<string, never>,
  SyncProviderPasswordChangeStatusResult
>(functions, 'syncProviderPasswordChangeStatus');

// ═══════════════════════════════════════════════════
// Bookings (Atomic operations)
// ═══════════════════════════════════════════════════

export interface MarkContactedInput {
  bookingId: string;
}

export interface MarkContactedResult {
  success: true;
  bookingId: string;
}

/**
 * 📞 المزود تواصل مع العميل — يحدّث حالة الطلب فقط (pending → contacted).
 *
 * ⚠️ لا يحجز التاريخ ولا يلمس التوفّر. الحجز إجراء منفصل (markDateAsBooked).
 * Authorization: admin / vendor / provider user (يتفحص في الـ function)
 */
export const callMarkContacted = httpsCallable<
  MarkContactedInput,
  MarkContactedResult
>(functionsMe, 'markContacted');

export interface RemindProviderInput {
  bookingId: string;
}

export interface RemindProviderResult {
  success: true;
  bookingId: string;
  recipientCount: number;
}

/**
 * ⏰ (Admin only) تذكير المزود بطلب متأخر — in-app notification + FCM push
 * لكل مستلمي الطلب (التاجر + فريق المزود). Rate limit: مرة كل ساعة لكل طلب.
 */
export const callRemindProvider = httpsCallable<
  RemindProviderInput,
  RemindProviderResult
>(functionsMe, 'remindProvider');

export interface MarkDateAsBookedInput {
  venueId: string;
  /** تاريخ المناسبة YYYY-MM-DD (يُبنى منه slot doc id: `${eventDate}_${timeSlot}`) */
  eventDate: string;
  /** الفترة — حالياً 'evening' فقط ('morning' محجوزة للمستقبل) */
  timeSlot?: string;
  /**
   * مُعرّف *مستند* الطلب الفائز في booking_requests (document id، وليس الـ display code).
   * مطلوب إلا إذا كان الحجز خارجياً بحتاً (externalBooking).
   */
  winningRequestDocId?: string;
  /** حجز خارج التطبيق بدون طلب فائز من التطبيق */
  externalBooking?: boolean;
}

export interface MarkDateAsBookedResult {
  success: true;
  /** عدد الطلبات المنافسة التي أُغلقت (booked_elsewhere) */
  closedCount: number;
  /** هل تم تحديث الطلب الفائز إلى completed_externally */
  winnerUpdated: boolean;
  externalBooking: boolean;
  bookedByRequesterUid: string | null;
  /** doc id لمستند booking_dates = `${eventDate}_${timeSlot}` */
  slotDocId: string;
  bookedByRequestDocId: string | null;
  bookedByRequestCode: string | null;
}

/**
 * 📅 "تم الحجز" — أهم إجراء في النموذج الجديد (lead-gen).
 *
 * في batch واحدة: يحوّل الطلب الفائز → completed_externally، يُغلق المنافسين
 * → booked_elsewhere، ويكتب توفّر التاريخ (booking_dates) → booked.
 * Authorization (مالك القاعة / أدمن) يُتفحص داخل الـ function.
 */
export const callMarkDateAsBooked = httpsCallable<
  MarkDateAsBookedInput,
  MarkDateAsBookedResult
>(functionsMe, 'markDateAsBooked');

export interface CreateManualBookingInput {
  bookingId: string;
  booking: Record<string, unknown>;
}

export interface CreateManualBookingResult {
  success: true;
  bookingId: string;
  slotDocId: string;
  closedCount: number;
}

/**
 * Creates a manual request directly as completed_externally and books its date
 * atomically. No pending document is committed at any point.
 */
export const callCreateManualBooking = httpsCallable<
  CreateManualBookingInput,
  CreateManualBookingResult
>(functionsMe, 'createManualBooking');

// ═══════════════════════════════════════════════════
// Reject Booking (H-3 fix)
// ═══════════════════════════════════════════════════

export interface RejectBookingInput {
  bookingId: string;
  reason: string;
}

export interface RejectBookingResult {
  success: true;
  bookingId: string;
}

/**
 * ❌ رفض حجز مع Firestore transaction
 *
 * 🛡️ H-3 fix: يمنع race conditions بين confirm/reject من managers متعددين
 */
export const callRejectBooking = httpsCallable<
  RejectBookingInput,
  RejectBookingResult
>(functionsMe, 'rejectBookingRequest');

export interface CancelBookingInput {
  bookingId: string;
  reason?: string;
}

export const callCancelBooking = httpsCallable<
  CancelBookingInput,
  RejectBookingResult
>(functionsMe, 'cancelBookingRequest');

// ═══════════════════════════════════════════════════
// Block Date (H-4 fix)
// ═══════════════════════════════════════════════════

export type BlockAction = 'block' | 'unblock';

export interface SetDateBlockInput {
  venueId: string;
  date: string; // YYYY-MM-DD
  action?: BlockAction; // 'block' افتراضياً
  reason?: string; // مطلوب عند action='block'
  /** تأكيد صريح من الواجهة قبل إلغاء الطلبات النشطة على التاريخ. */
  confirmCancelRequests?: boolean;
}

export interface SetDateBlockResult {
  success: true;
  venueId: string;
  date: string;
  action: BlockAction;
}

/**
 * ⚫🟢 حظر/فتح يوم مع Firestore transaction
 *
 * 🛡️ H-4 fix: يفحص atomically لا booking مؤكّد قبل الحظر، ويحذف
 *    مستند booking_dates عند الفتح (بدل deleteDoc client-side الهش).
 */
export const callSetDateBlock = httpsCallable<
  SetDateBlockInput,
  SetDateBlockResult
>(functionsMe, 'setDateBlock');

// ═══════════════════════════════════════════════════
// Provider Request Review (H-10 fix)
// ═══════════════════════════════════════════════════

export interface ReviewProviderRequestInput {
  requestId: string;
  action: 'approve' | 'reject';
  rejectionReason?: string;
}

export interface ReviewProviderRequestResult {
  success: true;
  requestId: string;
  action: 'approve' | 'reject';
}

/**
 * 🛡️ مراجعة طلب service_provider_request (admin only)
 *
 * 🛡️ H-10 fix: العملية تتم في Cloud Function مع server-side admin check،
 *    بدلاً من client-side assertAdmin اللي ممكن يلتف عليه.
 *    تكتب reviewedBy تلقائياً من context.auth.uid + audit log entry.
 */
export const callReviewProviderRequest = httpsCallable<
  ReviewProviderRequestInput,
  ReviewProviderRequestResult
>(functions, 'reviewProviderRequest');

// ═══════════════════════════════════════════════════
// 💎 Subscription Management (Phase 1)
// ═══════════════════════════════════════════════════

export interface ExtendTrialInput {
  providerId: string;
  daysToAdd: number;
  reason: string;
}

export interface MarkAsPaidInput {
  providerId: string;
  plan: 'basic' | 'pro' | 'enterprise';
  durationDays: number;
  paymentAmount: number;
  paymentNote: string;
}

export interface SuspendProviderInput {
  providerId: string;
  reason: string;
}

export interface SimpleSuccessResult {
  success: true;
}

/**
 * 💎 تمديد الفترة التجريبية (admin only)
 */
export const callExtendTrial = httpsCallable<
  ExtendTrialInput,
  SimpleSuccessResult
>(functions, 'extendTrial');

/**
 * 💳 تحويل provider لـ paid_active (manual payment)
 */
export const callMarkAsPaid = httpsCallable<
  MarkAsPaidInput,
  SimpleSuccessResult
>(functions, 'markAsPaid');

/**
 * 🚫 إيقاف provider (admin only)
 */
export const callSuspendProvider = httpsCallable<
  SuspendProviderInput,
  SimpleSuccessResult
>(functions, 'suspendProvider');

// ═══════════════════════════════════════════════════
// 📜 Audit Logs
// ═══════════════════════════════════════════════════

export interface LogAuditInput {
  action: string;
  description: string;
  severity?: 'info' | 'warning' | 'critical';
  resourceType?: string;
  resourceId?: string;
  resourceName?: string;
  metadata?: Record<string, unknown>;
}

export interface LogAuditResult {
  success: true;
  logId: string;
}

/**
 * 📜 تسجيل audit log entry (via Cloud Function — bypass rules)
 */
export const callLogAudit = httpsCallable<LogAuditInput, LogAuditResult>(
  functions,
  'logAudit'
);

// ═══════════════════════════════════════════════════
// 🗑️ Delete Provider Cascade
// ═══════════════════════════════════════════════════

export interface DeleteProviderCascadeInput {
  providerId: string;
  confirmationName: string;
  reason: string;
}

export interface DeleteProviderCascadeResult {
  success: boolean;
  providerName: string;
  deleted: {
    providerDoc: boolean;
    ownerAuth: boolean;
    providerUsers: { authDeleted: number; docsDeleted: number };
    venues: number;
    packages: number;
    bookingDates: number;
    reviews: number;
    bookingRequests: number;
    storageFiles: number;
    customers: number;
  };
  errors: string[];
}

/**
 * 🗑️ حذف كامل للـ provider (admin only)
 *
 * يحذف: Auth users + Venues + Packages + Bookings + Storage + Subcollections
 */
export const callDeleteProviderCascade = httpsCallable<
  DeleteProviderCascadeInput,
  DeleteProviderCascadeResult
>(functions, 'deleteProviderCascade');

export { functions };
